import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, BarChart } from "lucide-react";
import { SuccessMetric } from '@/types/journey';
import { useIsMobile } from '@/hooks/use-mobile';

interface SuccessMetricsFieldProps {
  label: string;
  metrics: SuccessMetric[];
  onChange: (metrics: SuccessMetric[]) => void;
  placeholder?: string;
}

const SuccessMetricsField: React.FC<SuccessMetricsFieldProps> = ({
  label,
  metrics,
  onChange,
  placeholder = "Add success metrics..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempMetrics, setTempMetrics] = useState<SuccessMetric[]>(metrics || []);
  const [newMetric, setNewMetric] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    setTempMetrics(metrics || []);
  }, [metrics]);

  const handleAddMetric = () => {
    if (!newMetric.trim()) return;
    
    const newMetricItem: SuccessMetric = {
      id: `metric-${Date.now()}`,
      text: newMetric.trim()
    };
    
    setTempMetrics([...tempMetrics, newMetricItem]);
    setNewMetric('');
  };

  const handleRemoveMetric = (id: string) => {
    setTempMetrics(tempMetrics.filter(metric => metric.id !== id));
  };

  const handleSave = () => {
    onChange(tempMetrics);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempMetrics(metrics || []);
    setIsEditing(false);
    setNewMetric('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddMetric();
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-2">
        <label className="text-[11px] font-medium block text-muted-foreground">
          {label}
        </label>
        
        <div className="space-y-2">
          {/* Metrics list */}
          <div className="flex flex-col gap-1">
            {tempMetrics.map((metric, index) => (
              <div 
                key={metric.id}
                className="group bg-purple-50 dark:bg-purple-900/20 text-xs px-2 py-1 rounded flex items-center gap-1 border border-purple-200 dark:border-purple-800/30"
              >
                <BarChart className="h-3 w-3 text-purple-500 dark:text-purple-400 mr-0.5" />
                <span className="text-muted-foreground mr-0.5">{index+1}.</span>
                <span>{metric.text}</span>
                <button
                  type="button"
                  className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto text-muted-foreground hover:text-foreground"
                  onClick={() => handleRemoveMetric(metric.id)}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          
          {/* Add new metric */}
          <div className="flex gap-1">
            <div className="flex-1 relative">
              <Input
                type="text"
                value={newMetric}
                onChange={(e) => setNewMetric(e.target.value)}
                placeholder={placeholder}
                className="text-xs"
                onKeyDown={handleKeyDown}
              />
              {newMetric && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-5 w-5 p-0"
                  onClick={() => setNewMetric('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={handleAddMetric}
              disabled={!newMetric.trim()}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          
          <div className="flex justify-end space-x-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {tempMetrics.length > 0 ? (
        <div 
          className="flex flex-col gap-1 min-h-[2.5rem] cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 p-1 rounded"
          onClick={() => {
            console.log('SuccessMetricsField clicking on non-empty field');
            setIsEditing(true);
          }}
        >
          {tempMetrics.map((metric, index) => (
            <div 
              key={metric.id}
              className="bg-purple-50 dark:bg-purple-900/20 text-xs px-2 py-1 rounded flex items-center gap-1 border border-purple-200 dark:border-purple-800/30"
            >
              <BarChart className="h-3 w-3 text-purple-500 dark:text-purple-400 mr-0.5" />
              <span className="text-muted-foreground mr-0.5">{index+1}.</span>
              {metric.text}
            </div>
          ))}
        </div>
      ) : (
        <div 
          className="text-xs min-h-[2.5rem] cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 p-1 rounded text-muted-foreground italic"
          onClick={() => {
            console.log('SuccessMetricsField clicking on empty field');
            setIsEditing(true);
          }}
        >
          {placeholder}
        </div>
      )}
    </div>
  );
};

export default SuccessMetricsField; 